import * as React from "react";
import styled, { css } from "styled-components";
import ReactTooltip from "react-tooltip";
import { capitalize } from "lodash";

import { Text } from "src/library/components/Text/Text";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { IUser } from "src/core/users/api/users.api";

interface IProps {
  /** Id of the row this component is showing the assigned user for */
  rowId: number;
  /** The assigned user */
  user?: IUser;
  /** The assigned group  */
  groupName?: string;
  /** justify-content setting the wrapping div */
  justification?: "start"|"center"|"space-between"|"space-around"| "space-evenly"
}

/**
 * @description Generates a styled assigned column for use by entities that may have users or groups assigned to them, such as support tickets and pending users.
 */
const AssignedUser: React.FC<IProps> = ({
  rowId, user, groupName, justification
}) => {
  const initials = React.useMemo(() => {
    if (user?.firstName && user?.lastName) return `${user.firstName[ 0 ]} ${user.lastName[ 0 ]}`.toUpperCase();

    if (user?.firstName && !user?.lastName) return user.firstName[ 0 ].toUpperCase();

    return "-";
  }, [ user ]);

  const displayName = React.useMemo(() => {
    if (user?.firstName && user?.lastName) return `${capitalize(user.firstName)} ${capitalize(user.lastName)}`;

    if (user?.firstName && !user?.lastName) return capitalize(user.firstName);

    return "-";
  }, [ user ]);

  // Used to determine width of the tool tip based on longest name
  const minWidthLength = React.useMemo(() => {
    let currentTextLength = 120;

    if (!groupName && !displayName) currentTextLength = 120;

    if (!groupName && displayName) currentTextLength = displayName.length;

    if (groupName && !displayName) currentTextLength = groupName.length;

    if (groupName && displayName) currentTextLength = (displayName.length > groupName.length ? displayName : groupName).length;

    // Text length multiplied by either 7, 8.5 or 12 in order to set width of tool tip.
    // There is likely a slightly more complex but cleaner mathmatical solution here however this covers majority of cases.
    return currentTextLength * (currentTextLength > 40 ? 7 : currentTextLength > 20 ? 8.5 : 12);
  }, [ displayName, groupName ]);

  // return dash if no assigned user passed
  if (!user) {
    return (
      <NoneAssignedWrapper>
        <Text>
          -
        </Text>
      </NoneAssignedWrapper>
    );
  }

  return (
    <AssignedWrapper minHoverContainerWidth={minWidthLength}>
      <div
        className="item"
        data-tip
        data-for={`row-${rowId}`}
        style={{ justifyContent: justification ?? "start" }}
      >
        <Icon
          icon={Icons.PERSON}
          size={24}
        />

        <Text>
          {initials}
        </Text>
      </div>

      <ReactTooltip
        id={`row-${rowId}`}
        place="bottom"
        className="assigned-tool-tip"
      >
        <OnHover
          displayName={displayName}
          groupName={groupName || null}
        />
      </ReactTooltip>
    </AssignedWrapper>
  );
};

const OnHover: React.FC<{displayName: string, groupName: string | null}> = ({ displayName, groupName }) => {
  return (
    <div>
      {groupName && (
        <div className="item">
          <Icon
            icon={Icons.GROUPS}
            $fillColor="white"
            size={24}
          />

          <Text color="white">
            {` ${groupName}`}
          </Text>
        </div>
      )}

      <div className="item">
        <Icon
          icon={Icons.PERSON}
          $fillColor="white"
          size={24}
        />

        <Text color="white">
          {` ${displayName}`}
        </Text>
      </div>
    </div>
  );
};

const AssignedWrapper = styled.div<{minHoverContainerWidth: number}>`
  ${({ theme }) => css`
    .item {
      position: relative;
      display: flex;
      flex-direction: row;
      align-items: center;

      span {
        margin-left: ${theme.spacing.margins.xs};
      }
    }

    .assigned-tool-tip {
      font-color: ${theme.colors.white};
      
      padding: ${theme.spacing.padding.md};
      opacity: 1;
    }
  `}
`;

const NoneAssignedWrapper = styled.div`
  ${({ theme }) => css`
    margin-left: ${theme.spacing.margins.sm};
  `}
`;

export default AssignedUser;
